/* eslint-disable @typescript-eslint/no-explicit-any */

declare const process: {
  env: {
    REACT_APP_API_URL?: string;
    REACT_APP_FRONT_URL?: string;
    NODE_ENV?: string;
  };
};

const isLocalhost =
    typeof window !== "undefined" &&
    (window.location.hostname === "localhost" ||
        window.location.hostname === "127.0.0.1");

const removeTrailingSlash = (value: string): string => {
  return value.replace(/\/+$/, "");
};

const assertProductionEndpoint = (name: string, value: string): string => {
  const normalized = removeTrailingSlash(value);

  if (!isLocalhost && /(^|[./-])(localhost|127\.0\.0\.1|sandbox)([.:/-]|$)/i.test(normalized)) {
    throw new Error(`${name} cannot use a local or sandbox endpoint in production.`);
  }

  return normalized;
};

export const URL = assertProductionEndpoint(
    "REACT_APP_API_URL",
    isLocalhost
        ? "http://localhost:8082"
        : process.env.REACT_APP_API_URL || "https://affiliates.eupago.pt"
);

export const URL_FRONT = assertProductionEndpoint(
    "REACT_APP_FRONT_URL",
    isLocalhost
        ? "http://localhost:3002"
        : process.env.REACT_APP_FRONT_URL || "https://partners.eupago.pt"
);

export const FRONT_URL = URL_FRONT;

type SuccessCallback<T = unknown> = (response: T) => void;
type FailureCallback = (error: Error) => void;

function buildHeaders(request: RequestInit = {}): Headers {
  const token = sessionStorage.getItem("jwt");
  const headers = new Headers(request.headers || undefined);

  if (token) {
    headers.set("Authorization", `Bearer ${token}`);
  }

  headers.set("Accept", "application/json");

  if (!(request.body instanceof FormData) && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }

  return headers;
}

async function parseResponse<T = unknown>(response: Response): Promise<T> {
  const contentType = response.headers.get("content-type") || "";

  if (response.status === 204) {
    return null as T;
  }

  if (contentType.includes("application/json")) {
    return response.json() as Promise<T>;
  }

  return (await response.text()) as T;
}

async function parseErrorResponse(response: Response): Promise<Error> {
  let message = `Erro HTTP ${response.status}`;

  try {
    const contentType = response.headers.get("content-type") || "";

    if (contentType.includes("application/json")) {
      const body = await response.json();

      message =
          body?.message ||
          body?.error ||
          body?.text ||
          JSON.stringify(body) ||
          message;
    } else {
      const text = await response.text();
      message = text || message;
    }
  } catch {
    // Mantém a mensagem genérica se não for possível ler a resposta.
  }

  return new Error(message);
}

export function makeHttpRequest<T = unknown>(
    url: string,
    request: RequestInit,
    success: SuccessCallback<T>,
    failure: FailureCallback
): void {
  fetch(url, {
    ...request,
    headers: buildHeaders(request),
  })
      .then(async (response) => {
        if (!response.ok) {
          throw await parseErrorResponse(response);
        }

        return parseResponse<T>(response);
      })
      .then((response) => success(response))
      .catch((error) => {
        failure(error instanceof Error ? error : new Error(String(error)));
      });
}

export async function requestJson<T = unknown>(
    url: string,
    init: RequestInit = {}
): Promise<T> {
  const response = await fetch(url, {
    ...init,
    headers: buildHeaders(init),
  });

  if (!response.ok) {
    throw await parseErrorResponse(response);
  }

  return parseResponse<T>(response);
}
